import { getRhgAppHeader } from '@/utils/browser';
import { MINUTE_MS } from '@calm/web-common/dist/time';
import createMediaV2Api from '@rhg/libs/api/create-media-v2-api';
import useAuth from '@rhg/libs/auth0/use-auth';
import { logger } from '@rhg/libs/logger';
import { invalidateQueryKey, queryKeys, useQuery } from '@rhg/libs/react-query';
import { useCallback, useMemo } from 'react';
import { useCircle } from '../use-circle';

const recentAndFavoriteGuidesQueryOptions = {
    queryKey: ({ circleId }: { circleId: number }) => {
        return [queryKeys.recentAndFavoriteGuides, circleId];
    },
    queryFn: async ({
        getAccessTokenSilently,
        circleId,
        circleAuthorizationToken,
        rHGApp,
    }: {
        getAccessTokenSilently: () => Promise<string>;
        circleId: number;
        circleAuthorizationToken: string;
        rHGApp: string;
    }) => {
        const mediaV2Api = createMediaV2Api(getAccessTokenSilently);

        const response = await mediaV2Api.v2GetClinicalProgramGuide({
            circleAuthorizationToken,
            recipientId: circleId,
            rHGApp,
        });

        return response.data;
    },
    queryOptions: ({
        isAuthenticated,
        circleId,
        circleAuthorizationToken,
        includeFetch,
    }: {
        isAuthenticated: boolean;
        circleId: number;
        circleAuthorizationToken: string;
        includeFetch: boolean;
    }) => {
        return {
            staleTime: 15 * MINUTE_MS, // 15 minutes since we invalidate on favorite and play start
            enabled: !!(
                includeFetch &&
                isAuthenticated &&
                circleId > 0 &&
                circleAuthorizationToken
            ),
        };
    },
};

export function useRecentAndFavoriteGuides(
    circleId: number,
    includeFetch = true
) {
    const { isAuthenticated, getAccessTokenSilently } = useAuth();
    const { circle } = useCircle(circleId);
    const circleAuthorizationToken = circle?.authorizationJWT ?? '';
    const { queryKey, queryFn, queryOptions } =
        recentAndFavoriteGuidesQueryOptions;
    const rHGApp = getRhgAppHeader();

    const key = useMemo(() => queryKey({ circleId }), [circleId, queryKey]);
    const queryOptionValues = queryOptions({
        isAuthenticated,
        circleId,
        circleAuthorizationToken,
        includeFetch,
    });

    const { data, ...otherProps } = useQuery({
        queryKey: key,
        queryFn: async () =>
            queryFn({
                getAccessTokenSilently,
                circleId,
                circleAuthorizationToken,
                rHGApp,
            }),
        ...queryOptionValues,
    });

    const refresh = useCallback(() => {
        invalidateQueryKey(key).catch((err: unknown) =>
            logger.error(
                'useRecentAndFavoriteGuides: failed to invalidate query',
                {},
                err as Error
            )
        );
    }, [key]);

    return {
        refresh,
        ...data,
        ...otherProps,
    };
}
